import { SmartInsight } from 'src/charts/SmartInsightsAIRecommendations'

export interface SmartInsightsResponse {
  hasInsights: boolean
  insights?: SmartInsight[]
}

// Mock data with 3+ insights per category (for testing scrolling)
export const mockSmartInsightsFullData: SmartInsightsResponse = {
  hasInsights: true,
  insights: [
    // Brand Health Signals - Strengths
    {
      aiRecommendation:
        'Continue engaging with positive comments and share user-generated content to maintain momentum.',
      category: 'brand_health_signals',
      id: '1',
      sources: [
        {
          links: [
            {
              date: '2026-02-15',
              language: 'en',
              url: 'https://instagram.com/post/1',
            },
          ],
          platform: 'Instagram',
          text: 'Love this brand! Best customer service ever.',
        },
      ],
      title: 'Strong Brand Sentiment on Instagram',
      type: 'strength',
    },
    {
      aiRecommendation:
        'Leverage these positive reviews in marketing materials and respond to maintain engagement.',
      category: 'brand_health_signals',
      id: '2',
      title: 'Positive Reviews Increasing',
      type: 'strength',
    },
    {
      aiRecommendation:
        'Create a loyalty program to reward repeat customers and increase retention.',
      category: 'brand_health_signals',
      id: '3',
      title: 'High Customer Loyalty',
      type: 'strength',
    },
    {
      aiRecommendation:
        'Encourage referrals with incentives to capitalize on organic growth.',
      category: 'brand_health_signals',
      id: '4',
      title: 'Strong Word-of-Mouth',
      type: 'strength',
    },

    // Brand Health Signals - Challenges
    {
      aiRecommendation:
        'Implement automated responses for common queries and increase support staff during peak hours.',
      category: 'brand_health_signals',
      id: '5',
      title: 'Response Time Concerns',
      type: 'challenge',
    },
    {
      aiRecommendation:
        'Review quality control processes and reach out to affected customers with solutions.',
      category: 'brand_health_signals',
      id: '6',
      title: 'Product Quality Complaints',
      type: 'challenge',
    },

    // Competitor Moves - Strengths
    {
      aiRecommendation:
        'Highlight competitive pricing in marketing campaigns and create comparison content.',
      category: 'competitor_moves',
      id: '7',
      title: 'Better Pricing Than Competitors',
      type: 'strength',
    },

    // Competitor Moves - Challenges
    {
      aiRecommendation:
        'Analyze competitor feature and consider similar implementation or differentiation strategy.',
      category: 'competitor_moves',
      id: '8',
      title: 'Competitor Launched New Feature',
      type: 'challenge',
    },
    {
      aiRecommendation:
        'Increase marketing budget and focus on unique value propositions.',
      category: 'competitor_moves',
      id: '9',
      title: 'Competitor Aggressive Marketing',
      type: 'challenge',
    },
    {
      aiRecommendation:
        'Conduct market analysis and develop retention strategies for existing customers.',
      category: 'competitor_moves',
      id: '10',
      title: 'Market Share Pressure',
      type: 'challenge',
    },

    // Influencer Opportunities - Strengths
    {
      aiRecommendation:
        'Partner with engaged micro-influencers for authentic brand promotion.',
      category: 'influencer_opportunities',
      id: '11',
      title: 'Micro-Influencer Engagement',
      type: 'strength',
    },
    {
      aiRecommendation:
        'Scale successful influencer partnerships and track conversion metrics.',
      category: 'influencer_opportunities',
      id: '12',
      title: 'High Influencer ROI',
      type: 'strength',
    },

    // Content Recommendations - Strengths
    {
      aiRecommendation:
        'Increase video content production and experiment with different formats.',
      category: 'content_recommendations',
      id: '13',
      title: 'Video Content Performing Well',
      type: 'strength',
    },

    // Content Recommendations - Challenges
    {
      aiRecommendation:
        'Test different posting times and content formats to improve engagement rates.',
      category: 'content_recommendations',
      id: '14',
      title: 'Low Engagement on Posts',
      type: 'challenge',
    },

    // Crisis Warnings - Challenges
    {
      aiRecommendation:
        'Address concerns immediately with transparent communication and action plan.',
      category: 'crisis_warnings',
      id: '15',
      title: 'Negative Sentiment Spike',
      type: 'challenge',
    },
    {
      aiRecommendation:
        'Set up crisis management team and prepare public response strategy.',
      category: 'crisis_warnings',
      id: '16',
      title: 'Customer Complaints Trending',
      type: 'challenge',
    },
  ],
}

// Mock data with fewer than 3 insights in some categories (for testing min-height)
export const mockSmartInsightsSparseData: SmartInsightsResponse = {
  hasInsights: true,
  insights: [
    // Brand Health Signals - Only 1 strength
    {
      aiRecommendation: 'Continue current engagement strategy.',
      category: 'brand_health_signals',
      id: '1',
      title: 'Strong Brand Sentiment',
      type: 'strength',
    },

    // Brand Health Signals - Only 2 challenges
    {
      aiRecommendation: 'Improve response time with automation.',
      category: 'brand_health_signals',
      id: '2',
      title: 'Response Time Issues',
      type: 'challenge',
    },
    {
      aiRecommendation: 'Address product quality concerns.',
      category: 'brand_health_signals',
      id: '3',
      title: 'Product Feedback',
      type: 'challenge',
    },

    // Competitor Moves - No strengths, 1 challenge
    {
      aiRecommendation: 'Monitor competitor moves closely.',
      category: 'competitor_moves',
      id: '4',
      title: 'Competitor Activity',
      type: 'challenge',
    },

    // Influencer Opportunities - 1 strength, no challenges
    {
      aiRecommendation: 'Expand influencer partnerships.',
      category: 'influencer_opportunities',
      id: '5',
      title: 'Influencer Engagement',
      type: 'strength',
    },
  ],
}

// Mock data with no insights (for testing empty state)
export const mockSmartInsightsEmptyData: SmartInsightsResponse = {
  hasInsights: false,
  insights: [],
}

// Mock data with only one category having insights
export const mockSmartInsightsSingleCategory: SmartInsightsResponse = {
  hasInsights: true,
  insights: [
    {
      aiRecommendation: 'Immediate action required.',
      category: 'crisis_warnings',
      id: '1',
      title: 'Crisis Alert',
      type: 'challenge',
    },
    {
      aiRecommendation: 'Monitor situation closely.',
      category: 'crisis_warnings',
      id: '2',
      title: 'Negative Trend',
      type: 'challenge',
    },
  ],
}
